import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTenant } from "@/contexts/TenantContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CreditCard } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useQuery } from "@tanstack/react-query";

const creditCardSchema = z.object({
  cardNumber: z.string().min(13, "Número do cartão inválido"),
  cardholderName: z.string().min(3, "Nome do titular é obrigatório"),
  expirationDate: z.string().regex(/^\d{2}\/\d{2}$/, "Formato: MM/AA"),
  securityCode: z.string().min(3, "CVV inválido").max(4),
  email: z.string().email("Email inválido"),
  cpf: z.string().min(11, "CPF inválido"),
});

type CreditCardFormData = z.infer<typeof creditCardSchema>;

interface CreditCardPaymentProps {
  onSuccess: () => void;
  planId?: string;
  couponCode?: string;
  displayAmount?: number;
}

export function CreditCardPayment({ onSuccess, planId = 'plus', couponCode: couponCodeProp = '', displayAmount }: CreditCardPaymentProps) {
  const { tenantId, refreshTenant } = useTenant();
  const [loading, setLoading] = useState(false);

  // Buscar dados do plano
  const { data: plan } = useQuery({
    queryKey: ["subscription-plan", planId],
    queryFn: async () => {
      if (!planId) return null;
      const { data, error } = await supabase
        .from("subscription_plans")
        .select("*")
        .eq("id", planId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!planId,
  });

  const planPrice = plan?.price || 0;
  const finalAmount = typeof displayAmount === 'number' ? displayAmount : planPrice;
  const showDiscount = finalAmount !== planPrice;
  const planName = plan?.name || planId;

  const form = useForm<CreditCardFormData>({
    resolver: zodResolver(creditCardSchema),
    defaultValues: {
      cardNumber: "",
      cardholderName: "",
      expirationDate: "",
      securityCode: "",
      email: "",
      cpf: "",
    },
  });
  

  const formatCardNumber = (value: string) => {
    return value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
  };

  const formatExpirationDate = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}`;
    }
    return cleaned;
  };

  const formatCPF = (value: string) => {
    return value.replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const onSubmit = async (data: CreditCardFormData) => {
    if (!tenantId) {
      toast.error("Tenant não identificado");
      return;
    }

    setLoading(true);
    try {
      const [month, year] = data.expirationDate.split('/');
      
      const { data: result, error } = await supabase.functions.invoke('create-mercadopago-subscription', {
        body: {
          tenant_id: tenantId,
          card_number: data.cardNumber.replace(/\s/g, ''),
          cardholder_name: data.cardholderName,
          expiration_month: parseInt(month),
          expiration_year: parseInt(`20${year}`),
          security_code: data.securityCode,
          email: data.email,
          cpf: data.cpf.replace(/\D/g, ''),
          plan_id: planId,
          coupon_code: couponCodeProp || undefined,
        }
      });

      if (error) throw error;

      if (result?.activated_without_payment) {
        toast.success(`Cupom aplicado, plano ${planName} ativado sem pagamento!`);
        await refreshTenant?.();
        onSuccess();
      } else if (result.success) {
        toast.success(`Assinatura criada com sucesso! ${planName} ativado!`);
        await refreshTenant?.();
        onSuccess();
      } else {
        throw new Error(result.error || "Erro ao criar assinatura");
      }
    } catch (error: any) {
      console.error("Erro ao processar pagamento:", error);
      toast.error("Erro ao processar pagamento: " + (error.message || "Erro desconhecido"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
      
      <Alert>
        <CreditCard className="h-4 w-4" />
        <AlertDescription>
          Assinatura recorrente mensal de {showDiscount ? (
            <span>
              <span className="line-through">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(planPrice)}</span> → {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(finalAmount)}
            </span>
          ) : (
            new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(finalAmount)
          )}. A cobrança será automática todo mês.
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <Label htmlFor="cardNumber">Número do Cartão</Label>
        <Input
          id="cardNumber"
          placeholder="1234 5678 9012 3456"
          maxLength={19}
          {...form.register("cardNumber")}
          onChange={(e) => {
            const formatted = formatCardNumber(e.target.value);
            form.setValue("cardNumber", formatted);
          }}
        />
        {form.formState.errors.cardNumber && (
          <p className="text-sm text-destructive">{form.formState.errors.cardNumber.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="cardholderName">Nome do Titular</Label>
        <Input
          id="cardholderName"
          placeholder="Como está no cartão"
          {...form.register("cardholderName")}
          onChange={(e) => {
            form.setValue("cardholderName", e.target.value.toUpperCase());
          }}
        />
        {form.formState.errors.cardholderName && (
          <p className="text-sm text-destructive">{form.formState.errors.cardholderName.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="expirationDate">Validade</Label>
          <Input
            id="expirationDate"
            placeholder="MM/AA"
            maxLength={5}
            {...form.register("expirationDate")}
            onChange={(e) => {
              const formatted = formatExpirationDate(e.target.value);
              form.setValue("expirationDate", formatted);
            }}
          />
          {form.formState.errors.expirationDate && (
            <p className="text-sm text-destructive">{form.formState.errors.expirationDate.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="securityCode">CVV</Label>
          <Input
            id="securityCode"
            type="password"
            placeholder="123"
            maxLength={4}
            {...form.register("securityCode")}
          />
          {form.formState.errors.securityCode && (
            <p className="text-sm text-destructive">{form.formState.errors.securityCode.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="seu@email.com"
          {...form.register("email")}
        />
        {form.formState.errors.email && (
          <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="cpf">CPF do Titular</Label>
        <Input
          id="cpf"
          placeholder="000.000.000-00"
          maxLength={14}
          {...form.register("cpf")}
          onChange={(e) => {
            const formatted = formatCPF(e.target.value);
            form.setValue("cpf", formatted);
          }}
        />
        {form.formState.errors.cpf && (
          <p className="text-sm text-destructive">{form.formState.errors.cpf.message}</p>
        )}
      </div>

      <Button
        type="submit"
        disabled={loading || finalAmount === 0}
        className="w-full"
        size="lg"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processando...
          </>
        ) : finalAmount === 0 ? (
          <>
            <CreditCard className="mr-2 h-4 w-4" />
            Cupom aplicado - Sem pagamento necessário
          </>
        ) : (
          <>
            <CreditCard className="mr-2 h-4 w-4" />
            {showDiscount
              ? `Confirmar - ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(finalAmount)}`
              : "Confirmar Assinatura"
            }
          </>
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        Seus dados estão seguros. Processado pelo Mercado Pago.
      </p>
    </form>
  );
}
